import { FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useInjuryEndpoints } from '../../../hooks/useInjuryEndpoints';
import GenericModal from '../../../shared/GenericModal/GenericModal';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import { CustomFormV3 } from '../../../shared/CustomFormV3/CustomForm';
import { FieldDef } from '../../../shared/CustomFormV3/types';

interface InjuryReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  injuryId: string;
  athleteId: string;
}

interface IInjuryReturnForm {
  returnDate: Date;
  notes: string;
}

const schema = yup.object({
  returnDate: yup
    .date()
    .typeError('Inserisci la data di ritorno')
    .required('Inserisci la data di ritorno'),
  notes: yup.string().nullable(),
});

const layout: FieldDef[] = [
  { key: 'returnDate', type: 'date', label: 'Data di ritorno *', size: 12 },
  { key: 'notes', type: 'text-area', label: 'Note (opzionale)', placeholder: 'Es: rientro completo, nessuna limitazione...', size: 12 },
];

export const InjuryReturnModal: FC<InjuryReturnModalProps> = ({ isOpen, onClose, injuryId, athleteId }) => {
  const endpoints = useInjuryEndpoints();
  const queryClient = useQueryClient();

  const methods = useForm<IInjuryReturnForm>({
    defaultValues: { returnDate: new Date(), notes: '' },
    resolver: yupResolver(schema) as any,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (form: IInjuryReturnForm) =>
      endpoints.registerInjuryReturn(injuryId, {
        returnDate: form.returnDate.toISOString().split('T')[0],
        notes: form.notes || undefined,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['athlete-injuries', athleteId] });
      toast.success('Ritorno registrato');
      onClose();
    },
    onError: () => {
      toast.error('Errore durante il salvataggio. Riprova.');
    },
  });

  const onSubmit = (form: IInjuryReturnForm) => mutate(form);

  return (
    <FormProvider {...methods}>
      <GenericModal isOpen={isOpen} onClose={onClose} isLoading={isPending} size="sm" title="Registra ritorno">
        <div className="d-flex flex-column gap-3">
          <CustomFormV3 layout={layout} />
          <div className="d-flex justify-content-end gap-2 mt-2">
            <CustomButton color="outline" disabled={isPending} onClick={onClose}>
              Annulla
            </CustomButton>
            <CustomButton color="secondary" isLoading={isPending} onClick={methods.handleSubmit(onSubmit)}>
              Conferma ritorno
            </CustomButton>
          </div>
        </div>
      </GenericModal>
    </FormProvider>
  );
};

export default InjuryReturnModal;
